/// <amd-module name="Widgets/Burndown/BurndownWidgetComponents/BurndownDataFactory" />
import { WorkItemStateCategoryNames } from "./BurndownDataContract";
import { CurrentWorkItemAggregateEffort, WorkItemEffort } from "./BurndownDataContract";

/**
 * Used for converting widget settings and data from the Analytics Service into various forms
 * for consumption by other classes.
 *
 * TODO: Right now this class just has some utility methods in it. More refactoring needs to be done
 * to move data calculations from other locations to here.
 */
export class BurndownDataFactory {
    public static getCompletedEffort(effort: WorkItemEffort[], showResolvedItemsAsCompletedEnabled: boolean = false): WorkItemEffort[] {
        if (effort == null) {
            throw new Error("effort must be defined"); // Dev-only input safety check. No meaningful error for customer.
        }

        var completedEffort: WorkItemEffort[];

        if(showResolvedItemsAsCompletedEnabled){
            completedEffort = effort.filter(e => e.StateCategory === WorkItemStateCategoryNames.Completed || e.StateCategory === WorkItemStateCategoryNames.Resolved || e.State === WorkItemStateCategoryNames.Resolved);
        }
        else{
            completedEffort = effort.filter(e => e.StateCategory === WorkItemStateCategoryNames.Completed);
        }

        return completedEffort;
    }

    public static getRemainingEffort(effort: WorkItemEffort[], showResolvedItemsAsCompletedEnabled: boolean = false): WorkItemEffort[] {
        if (effort == null) {
            throw new Error("effort must be defined"); // Dev-only input safety check. No meaningful error for customer.
        }

        var remainingEffort: WorkItemEffort[];

        if(showResolvedItemsAsCompletedEnabled){
            remainingEffort = effort.filter(e => e.StateCategory !== WorkItemStateCategoryNames.Completed && e.StateCategory !== WorkItemStateCategoryNames.Resolved && e.State !== WorkItemStateCategoryNames.Resolved);
        }
        else{
            remainingEffort = effort.filter(e => e.StateCategory !== WorkItemStateCategoryNames.Completed);
        }
       
        return remainingEffort;
    }

    public static getEffortByDate(effort: WorkItemEffort[]): { [key: string]: number } {
        // For each date, sum the Aggregated Effort
        const datesToEffortDictionary: { [key: string]: number } = {};
        effort.forEach(e => {
            const date = e.Date;
            if (datesToEffortDictionary[date] == null) {
                datesToEffortDictionary[date] = e.AggregatedEffort;
            } else {
                datesToEffortDictionary[date] += e.AggregatedEffort;
            }
        });

        return datesToEffortDictionary;
    }

    /**
     * Calculates the average change for the given dates and effort
     * @param datesToEffortDictionary Dates in the format 'yyyy-MM-dd' are the keys, and the values are aggregated effort
     * @param dateKeys The dates, ordered from earliest to latest, to iterate over for calculating burndown from the given dictionary
     */
    public static getAverageChange(datesToEffortDictionary: { [key: string]: number }, dateKeys: string[]): number {
        // Return early to avoid dividing by 0 at the end of the method
        if (dateKeys.length <= 1) {
            return 0;
        }

        const start = datesToEffortDictionary[dateKeys[0]];
        const end = datesToEffortDictionary[dateKeys[dateKeys.length - 1]];

        // Slope of a line equation
        return (end - start) / (dateKeys.length - 1);
    }

    public static getSumOfAggregatedEffort(workItemEffort: CurrentWorkItemAggregateEffort[]): number {
        let total = 0;
        workItemEffort.forEach(effort => {
            total += effort.AggregatedEffort;
        });

        return total;
    }

    public static formatAverageBurn(averageBurn: number): number {
        let formattedNumber = 0;
        if (averageBurn < 1 && averageBurn > -1) {
            formattedNumber = Math.round(averageBurn * 10) / 10;
        } else {
            formattedNumber = Math.round(averageBurn);
        }

        return formattedNumber;
    }

    public static getCompletedPercentage(currentEffort: CurrentWorkItemAggregateEffort[], showResolvedItemsAsCompletedEnabled: boolean = false): number {
        // Calculate total remaining effort
        let completedEffort = 0;
        let totalEffort = 0;
        currentEffort.forEach(e => {
            if ((showResolvedItemsAsCompletedEnabled && (e.StateCategory === WorkItemStateCategoryNames.Resolved || e.State === WorkItemStateCategoryNames.Resolved))
                || (e.StateCategory === WorkItemStateCategoryNames.Completed))
            {
                completedEffort += e.AggregatedEffort;
            }

            totalEffort += e.AggregatedEffort;
        });

        if (totalEffort === 0) {
            return 0;
        } else {
            return Math.floor((completedEffort * 100) / totalEffort);
        }
    }

    public static isWorkItemCompleted(stateCategory: string, state: string, showResolvedItemsAsCompleted = false){
        if(stateCategory === WorkItemStateCategoryNames.Completed) return true;

        if(showResolvedItemsAsCompleted){
            return stateCategory === WorkItemStateCategoryNames.Resolved || state === WorkItemStateCategoryNames.Resolved;
        }

        return false;
    }
}
